<template>
  <div class="flex flex-col">
    <!-- WIP banner -->
    <div class="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-900">
      <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="mr-1.5 text-yellow-600" />
      <span class="font-semibold">Beta.</span>
      Funnel counts are live; the financials are estimates (marked
      <span class="px-1 py-px text-[9px] font-semibold uppercase rounded align-middle" style="background:#FFF7ED;color:#C2410C;">est.</span>)
      — not booked dollars. See the cost note &amp; data caveats below.
    </div>

    <!-- Header -->
    <div
      class="px-4 py-2.5 bg-white flex items-center gap-3 sticky top-0 z-30"
      style="border-bottom: 1px solid #E8E9E9;"
    >
      <h2 class="text-lg font-semibold flex items-center gap-1.5" style="color: #353537;">
        Program Economics
        <span class="text-xs font-medium text-yellow-700 ml-1">(beta)</span>
        <!-- Grain info popover -->
        <span
          class="relative inline-flex font-normal"
          @mouseenter="infoHover = true"
          @mouseleave="infoHover = false"
        >
          <button
            type="button"
            class="inline-flex items-center justify-center w-4 h-4"
            style="color: #A5A5A6;"
            aria-label="How these funnels fit together"
            @click="infoPinned = !infoPinned"
          >
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
          <div v-if="infoPinned" class="fixed inset-0 z-20" @click="infoPinned = false"></div>
          <div
            v-if="infoHover || infoPinned"
            class="absolute top-full left-0 mt-1 z-30 p-3 rounded-md bg-white text-left"
            style="border: 1px solid #E8E9E9; box-shadow: 0 4px 12px rgba(0,0,0,0.10); width: 340px;"
          >
            <div class="text-xs font-semibold mb-1" style="color: #353537;">How these funnels fit together</div>
            <p class="text-xs leading-relaxed" style="color: #626264;">
              All three funnels describe a <span class="font-semibold">single opportunity cohort</span>
              for the selected year. The opportunity funnel defines the cohort; the task and code funnels
              show the tasks and codes on <span class="font-semibold">those same opportunities</span> —
              regardless of when the work happened — so they reconcile (1LR tasks ≈ opportunities;
              codes ≈ the opportunity funnel's code box).
            </p>
          </div>
        </span>
      </h2>
      <span class="text-xs" style="color: #626264;">
        Opportunity · task · code funnels, plus a scenario simulator. Levers seed from live actuals.
      </span>
      <div class="flex-1"></div>
      <button
        class="px-2.5 py-1 text-xs rounded-md"
        style="border: 1px solid #E8E9E9; color: #353537;"
        @click="resetScenario"
      >
        Reset to actuals
      </button>
    </div>

    <!-- Filters -->
    <div
      class="px-4 py-2 bg-white flex items-center gap-4 flex-wrap"
      style="border-bottom: 1px solid #E8E9E9;"
    >
      <div class="flex items-center gap-1.5">
        <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Program</label>
        <select
          v-model="program"
          class="px-2 py-1 text-xs rounded-md bg-white focus:outline-none"
          style="border: 1px solid #E8E9E9; color: #353537;"
          @change="fetchActuals"
        >
          <option v-for="p in programOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
        </select>
      </div>

      <div class="flex items-center gap-1.5">
        <label class="text-xs whitespace-nowrap" style="color: #A5A5A6;">Year</label>
        <div class="flex rounded-md overflow-hidden" style="border: 1px solid #E8E9E9;">
          <button
            v-for="opt in yearOptions"
            :key="String(opt.value)"
            class="px-3 py-1 text-xs"
            :style="year === opt.value
              ? 'background:#FEF3F0;color:#F76734;font-weight:600;'
              : 'background:#fff;color:#353537;'"
            @click="setYear(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <div class="flex items-center gap-1.5">
        <label
          class="text-xs whitespace-nowrap" style="color: #A5A5A6;"
          title="Performance year = what year the coding counts for (VALID_THRU). Activity year = when the work happened in the calendar year (matches Operations V2)."
        >Scope</label>
        <div class="flex rounded-md overflow-hidden" style="border: 1px solid #E8E9E9;">
          <button
            v-for="opt in scopeOptions"
            :key="opt.value"
            class="px-3 py-1 text-xs"
            :style="scope === opt.value
              ? 'background:#FEF3F0;color:#F76734;font-weight:600;'
              : 'background:#fff;color:#353537;'"
            @click="setScope(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <label
        class="flex items-center gap-1.5 text-xs cursor-pointer" style="color: #626264;"
        title="Include opportunities that expired (timed out / closed unworked, e.g. the year-end bulk close). Off = matches Operations V2's default."
      >
        <input v-model="includeExpired" type="checkbox" @change="fetchActuals" />
        Include expired
      </label>

    </div>

    <!-- Body -->
    <div v-if="loading" class="p-8 text-sm" style="color: #626264;">Loading actuals…</div>
    <div v-else-if="error" class="p-8 text-sm text-red-600">{{ error }}</div>

    <div v-else-if="actuals" class="p-4 grid grid-cols-1 lg:grid-cols-5 gap-4">
      <!-- Funnels -->
      <div class="lg:col-span-3 flex flex-col gap-4">
        <div class="flex items-baseline gap-2">
          <span class="text-xs font-semibold uppercase tracking-wide" style="color:#626264;">Actuals</span>
          <span class="text-[11px]" style="color:#A5A5A6;">live counts — not estimates</span>
        </div>
        <!-- 1. Opportunity funnel -->
        <div class="bg-white rounded border border-gray-200 p-4">
          <div class="text-sm font-semibold mb-3" style="color: #353537;">
            1 · Opportunity funnel
          </div>
          <div class="flex flex-col gap-2">
            <div v-for="s in funnelRows(actuals.opp3)" :key="s.key" class="flex items-center gap-3">
              <div class="text-xs text-right" style="width: 150px; color: #626264;">{{ s.label }}</div>
              <div class="flex-1" style="height: 22px; background:#F4F5F5; border-radius:4px; overflow:hidden;">
                <div v-if="s.bands" class="flex h-full" :style="`width:${s.pct}%;`">
                  <div
                    v-for="b in s.bands" :key="b.key"
                    :style="`width:${b.pctOfRow}%; background:${b.color};`"
                    :title="`${b.label}: ${b.count.toLocaleString()}`"
                  ></div>
                </div>
                <div v-else class="h-full rounded" :style="`width:${s.pct}%; background:${palette.volume};`"></div>
              </div>
              <div class="text-xs text-right font-medium" style="width: 80px; color: #353537;">{{ s.count.toLocaleString() }}</div>
              <div
                class="text-xs text-right" style="width: 64px; color: #A5A5A6;"
                :title="convTitle(s)"
              >
                <span v-if="!s.isTop && s.share != null">{{ s.share }}%</span>
              </div>
            </div>
          </div>
          <div v-if="producedSplit" class="text-[11px] mt-2" style="color:#A5A5A6;">
            “Produced ≥1 code” spans both stages (not a sub-step of Completed):
            <span style="color:#0E8C9E;font-weight:600;">{{ producedSplit.completed.toLocaleString() }}</span> on completed ·
            <span style="color:#93B2CB;font-weight:600;">{{ producedSplit.open.toLocaleString() }}</span> on open opps.
          </div>
        </div>

        <!-- 2. Task funnel (opened vs closed, per tier) -->
        <div class="bg-white rounded border border-gray-200 p-4">
          <div class="text-sm font-semibold mb-1" style="color: #353537;">
            2 · Task funnel
          </div>
          <div class="flex items-center flex-wrap gap-x-3 gap-y-1 mb-2 text-xs" style="color: #626264;">
            <span
              v-for="seg in taskLegend" :key="seg.key"
              class="flex items-center gap-1 cursor-pointer select-none"
              :style="hiddenStatuses.includes(seg.key) ? 'opacity:0.35;text-decoration:line-through;' : ''"
              :title="hiddenStatuses.includes(seg.key) ? 'Show ' + seg.label : 'Hide ' + seg.label"
              @click="toggleStatus(seg.key)"
            >
              <span class="inline-block" :style="`width:10px;height:10px;border-radius:2px;background:${seg.color};`"></span>{{ seg.label }}
            </span>
          </div>
          <div class="flex flex-col gap-2">
            <div v-for="(t, ti) in actuals.tasks3" :key="t.tier" class="flex items-center gap-3">
              <div class="text-xs text-right" style="width: 150px; color: #626264;">{{ t.tier }}</div>
              <div class="flex-1" style="height: 22px; background:#F4F5F5; border-radius:4px; overflow:hidden;">
                <div class="flex h-full" :style="`width:${Math.round((visibleOpened(t) / maxTierOpened) * 100)}%;`">
                  <div
                    v-for="seg in taskSegments(t)" :key="seg.key"
                    :style="`width:${seg.pct}%; background:${seg.color};`"
                    :title="`${seg.label}: ${seg.count.toLocaleString()}`"
                  ></div>
                </div>
              </div>
              <div class="text-xs text-right font-medium" style="width: 80px; color: #353537;">{{ visibleOpened(t).toLocaleString() }}</div>
              <div
                class="text-xs text-right" style="width: 64px; color: #A5A5A6;"
                :title="ti === 0 ? `Base tier (${t.tier})` : `${tierSharePct(t)}% of ${baseTierLabel}`"
              >
                <span v-if="ti !== 0">{{ tierSharePct(t) }}%</span>
              </div>
            </div>
          </div>
          <!-- aggregate across tiers -->
          <div class="mt-3 pt-3" style="border-top: 1px solid #E8E9E9;">
            <div class="flex items-center gap-3">
              <div class="text-xs text-right font-semibold" style="width: 150px; color: #353537;">All</div>
              <div class="flex-1" style="height: 22px; background:#F4F5F5; border-radius:4px; overflow:hidden;">
                <div class="flex h-full" style="width: 100%;">
                  <div
                    v-for="seg in taskSegments(taskAggregate)" :key="seg.key"
                    :style="`width:${seg.pct}%; background:${seg.color};`"
                    :title="`${seg.label}: ${seg.count.toLocaleString()}`"
                  ></div>
                </div>
              </div>
              <div class="text-xs text-right font-semibold" style="width: 80px; color: #353537;">{{ taskAggregate.opened.toLocaleString() }}</div>
              <div style="width: 64px;"></div>
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs" style="color: #626264;">
              <span v-for="seg in taskSegments(taskAggregate)" :key="seg.key">
                {{ seg.label }}: <span style="color: #353537; font-weight: 600;">{{ seg.count.toLocaleString() }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- 3. Code funnel -->
        <div class="bg-white rounded border border-gray-200 p-4">
          <div class="text-sm font-semibold mb-3" style="color: #353537;">
            3 · Code funnel
          </div>
          <div class="flex flex-col gap-2">
            <div v-for="s in funnelRows(actuals.code3)" :key="s.key" class="flex items-center gap-3">
              <div class="text-xs text-right" style="width: 150px; color: #626264;" :title="codeTip(s.key)">
                <span v-if="s.sub" style="color: #A5A5A6;">↳ </span>{{ s.label }}
              </div>
              <div class="flex-1" style="height: 22px; background:#F4F5F5; border-radius:4px; overflow:hidden;">
                <div class="h-full rounded" :style="`width:${s.pct}%; background:${codeColor(s.key)};`"></div>
              </div>
              <div class="text-xs text-right font-medium" style="width: 80px; color: #353537;">{{ s.count.toLocaleString() }}</div>
              <div
                class="text-xs text-right" style="width: 64px; color: #A5A5A6;"
                :title="s.isTop ? 'All identified codes — 100%' : `${s.share}% of codes identified`"
              >
                <span v-if="!s.isTop && s.share != null">{{ s.share }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. Coder throughput — the productivity that seeds the cost levers -->
        <div v-if="actuals.throughput && actuals.throughput.length" class="rounded border p-3 mt-1" style="border-color:#EEF0F1; background:#FAFAFB;">
          <div class="text-xs font-semibold mb-1" style="color: #626264;">
            Coder throughput
            <span class="font-normal" style="color: #A5A5A6;">— fyi · human-attributed productivity per tier</span>
          </div>
          <div class="text-xs mb-2" style="color: #A5A5A6;">
            Over completed, coder-attributed tasks in this cohort.
            <span style="color:#626264;">Charts/coder-day</span> = distinct tasks finished ÷ distinct (coder × completion-date);
            <span style="color:#626264;">codes/coder-day</span> = distinct codes produced ÷ distinct (coder × code-date).
            Seeds the scenario's reviews/day &amp; reviews-per-opportunity levers.
          </div>
          <table class="w-full text-xs">
            <thead>
              <tr style="color:#A5A5A6;">
                <th class="text-left font-normal py-1">Tier</th>
                <th class="text-right font-normal">Charts / coder-day</th>
                <th class="text-right font-normal">Codes / coder-day</th>
                <th class="text-right font-normal">Median TAT (days)</th>
                <th class="text-right font-normal">Coders</th>
                <th class="text-right font-normal">Coder-days</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in actuals.throughput" :key="r.tier" style="color:#353537;">
                <td class="py-1">{{ r.tier }}</td>
                <td class="text-right">{{ r.tasks_per_coder_day ?? '—' }}</td>
                <td class="text-right">{{ r.codes_per_coder_day ?? '—' }}</td>
                <td class="text-right">{{ r.median_tat_days ?? '—' }}</td>
                <td class="text-right">{{ r.coders }}</td>
                <td class="text-right">{{ (r.coder_days || 0).toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Scenario & projected outcome -->
      <div class="lg:col-span-2 bg-white rounded border border-gray-200 p-4">
        <div class="text-xs font-semibold uppercase tracking-wide mb-3" style="color: #626264;">Scenario &amp; projected outcome</div>

        <!-- Projected outcome — answer-first: the headline result sits above the inputs -->
        <div class="rounded-lg p-4 mb-4" style="background:#EFF9FA; border:1px solid #BFE5E8; border-left:4px solid #0E8C9E;">
          <div class="mb-3">
            <span class="text-sm font-semibold" style="color:#0E8C9E;">Projected outcome</span>
          </div>

          <div class="mb-1">
            <div class="text-[11px] uppercase tracking-wide mb-0.5" style="color:#626264;">Net value</div>
            <div class="flex items-center gap-2">
              <span class="text-3xl font-semibold leading-none" :style="`color:${baseBand.net >= 0 ? '#15803D' : '#DC2626'};`">{{ moneyShort(baseBand.net) }}</span>
              <span class="px-1 py-px text-[10px] font-semibold uppercase rounded" style="background:#FFF7ED;color:#C2410C;" title="Estimate — derived from editable assumptions, not booked dollars">est.</span>
            </div>
          </div>
          <div class="text-[11px] mb-3" style="color:#626264;">
            <span style="color:#353537;font-weight:600;">{{ baseBand.roi ? baseBand.roi.toFixed(1) + '×' : '—' }}</span> value-to-cost ·
            <span style="color:#0E8C9E;">{{ moneyShort(baseBand.realized) }}</span> realized −
            <span :title="outputRows[3].tip">{{ outputRows[3].value }}</span> operating cost ·
            base {{ pct(scenario.acoRealization) }} realization
          </div>

          <!-- Sensitivity: the bottom line swings on ACO realization, so show a range -->
          <div class="pt-3" style="border-top:1px solid #D8EEF0;">
            <div class="text-[11px] font-semibold uppercase tracking-wide mb-1" style="color:#626264;">By ACO realization</div>
            <table class="w-full text-xs">
              <thead>
                <tr>
                  <th class="text-left font-normal py-0.5"></th>
                  <th
                    v-for="c in scenarioBand" :key="c.key"
                    class="text-right font-normal py-0.5 align-bottom"
                    :style="c.key === 'base' ? 'color:#353537;font-weight:600;' : 'color:#A5A5A6;'"
                  >
                    {{ c.label }}
                    <div class="font-normal" style="color:#A5A5A6;">{{ pct(c.realization) }}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in bandMetrics" :key="m.label">
                  <td class="py-0.5 text-left" style="color:#626264;">
                    <span
                      class="relative inline-flex items-center gap-1"
                      style="cursor: help;"
                      @mouseenter="hoveredOutputKey = 'band:' + m.label"
                      @mouseleave="hoveredOutputKey = null"
                    >{{ m.label }}
                      <span style="color:#A5A5A6;">ⓘ</span>
                      <span
                        v-if="hoveredOutputKey === 'band:' + m.label"
                        class="absolute z-40 p-2 rounded-md text-left"
                        style="bottom: 100%; left: 0; margin-bottom: 6px; width: 250px; background:#fff; border:1px solid #E8E9E9; box-shadow:0 4px 12px rgba(0,0,0,0.12); color:#626264; font-weight:400; line-height:1.5; white-space:normal;"
                      >{{ m.tip }}</span>
                    </span>
                  </td>
                  <td
                    v-for="c in scenarioBand" :key="c.key"
                    class="py-0.5 text-right" :style="bandCellStyle(m, c)"
                  >{{ bandCellValue(m, c) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Funnel → value chain (supporting detail) -->
          <div class="mt-3 pt-3 text-[11px]" style="border-top:1px solid #D8EEF0; color:#A5A5A6;">
            Codes
            <span :title="outputRows[0].tip">found <span style="color:#353537;font-weight:600;">{{ outputRows[0].value }}</span></span> ·
            <span :title="outputRows[1].tip">accepted <span style="color:#353537;font-weight:600;">{{ outputRows[1].value }}</span></span> ·
            <span :title="outputRows[2].tip">recoded <span style="color:#353537;font-weight:600;">{{ outputRows[2].value }}</span></span>
          </div>
        </div>

        <div class="text-sm font-semibold mb-2" style="color: #353537;">Scenario levers</div>

        <!-- Basis toggle: project the full cohort vs. only the work done so far -->
        <div class="flex rounded-md overflow-hidden mb-1" style="border: 1px solid #E8E9E9;">
          <button
            v-for="opt in modeOptions"
            :key="opt.value"
            class="flex-1 px-2 py-1 text-xs"
            :style="mode === opt.value ? 'background:#FEF3F0;color:#F76734;font-weight:600;' : 'background:#fff;color:#353537;'"
            @click="setMode(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
        <div class="text-[11px] mb-3" style="color:#A5A5A6;">
          {{ mode === 'projection'
            ? 'End-of-year: observed rates applied to the full cohort (every opportunity worked).'
            : 'Work done so far: observed rates on the opportunities reviewed to date.' }}
        </div>

        <div class="flex flex-col gap-2.5">
          <template v-for="lever in leverDefs" :key="lever.key">
            <div v-if="lever.divider" class="flex items-center gap-2 mt-2 mb-0.5">
              <span class="text-[10px] font-semibold uppercase tracking-wide" style="color:#A5A5A6;">{{ lever.divider }}</span>
              <div class="flex-1" style="border-top:1px solid #E8E9E9;"></div>
            </div>
            <div class="flex items-center justify-between gap-2">
              <label class="text-xs flex items-center gap-1" style="color: #626264;">
                {{ lever.label }}
                <span
                  v-if="lever.tip || lever.derived"
                  class="relative inline-flex"
                  style="color:#A5A5A6; cursor: help;"
                  @mouseenter="hoveredLeverKey = lever.key"
                  @mouseleave="hoveredLeverKey = null"
                >ⓘ
                  <span
                    v-if="hoveredLeverKey === lever.key"
                    class="absolute z-40 p-2 rounded-md text-left"
                    style="bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 6px; width: 250px; background:#fff; border:1px solid #E8E9E9; box-shadow:0 4px 12px rgba(0,0,0,0.12); color:#626264; font-weight:400; line-height:1.5; white-space:normal;"
                  >{{ lever.derived ? dollarPerCodeTip : lever.tip }}</span>
                </span>
                <span
                  v-if="lever.assumption"
                  class="px-1 py-px text-[9px] font-semibold uppercase rounded"
                  style="background:#FFF7ED;color:#C2410C;"
                  title="Estimate — an editable assumption, not yet measured"
                >est.</span>
              </label>
              <!-- Computed value (no field) — plain teal text reads as "derived, not editable".
                   Padded to land on the same number column as the editable fields below. -->
              <div v-if="lever.derived" class="flex-shrink-0 text-right" style="width: 120px; padding-right: 27px;">
                <span class="text-sm font-semibold" style="color:#0E8C9E;">{{ money(dollarPerCode) }}</span>
              </div>
              <!-- Editable field — filled "well". Number sits in a shared right-aligned column;
                   the unit ($ / % / /hr) is a fixed slot to its right, so every value lines up. -->
              <div
                v-else
                class="flex items-center flex-shrink-0 rounded-md bg-[#F7F8F9] border border-[#D7DBDD] hover:border-[#A5A5A6] focus-within:bg-white focus-within:border-[#0E8C9E] focus-within:ring-2 focus-within:ring-[#0E8C9E]/20"
                style="width: 120px;"
              >
                <span v-if="lever.prefix" class="pl-2 text-xs flex-shrink-0" style="color:#A5A5A6;">{{ lever.prefix }}</span>
                <input
                  :value="leverDisplay(lever)"
                  :type="lever.thousands ? 'text' : 'number'"
                  :inputmode="lever.thousands ? 'numeric' : null"
                  min="0"
                  :step="lever.thousands ? null : lever.step"
                  class="flex-1 min-w-0 bg-transparent py-1 pl-2 text-xs text-right focus:outline-none"
                  style="color: #353537;"
                  @focus="focusedLeverKey = lever.key"
                  @blur="focusedLeverKey = null"
                  @input="onLeverInput(lever, $event.target.value)"
                />
                <span class="text-xs text-left flex-shrink-0" style="width: 27px; padding-left: 3px; color:#A5A5A6;">{{ lever.suffix || '' }}</span>
              </div>
            </div>
          </template>
        </div>

        <div class="mt-4 p-2 rounded text-xs" style="background:#FFF7ED; color:#9A3412;">
          <span class="px-1 py-px text-[9px] font-semibold uppercase rounded mr-1" style="background:#fff;color:#C2410C;">est.</span><span class="font-semibold">Financials are estimates — ROI hinges on one assumption.</span>
          The whole result rests on the <span class="font-semibold">ACO realization %</span> slider
          (~10%), the least-grounded input: unlike MA, an ACO doesn't convert coding 1:1 — MSSP only lifts the
          benchmark for coding up to a +3% coding-intensity cap, and REACH's coding-intensity factor largely neutralizes it, so a
          defensible range is ~5–15% and the ROI is roughly one slider from &lt;1.0×. The $10.4k/RAF base is the
          MA figure (ACO FFS ≈ $13.6k, partly offsetting). Re-bill (40%) is an assumption pending the claims join.
          Only <span class="font-semibold">single-year</span> value is counted — multi-year HCC persistence is
          uncounted (and REACH ends PY2026). Capture must stay RADV-defensible.
        </div>

        <div class="text-[11px] mt-3" style="color:#A5A5A6;">
          <span style="color:#626264;">ⓘ</span> hover for the formula ·
          <span class="px-1 py-px text-[9px] font-semibold uppercase rounded" style="background:#FFF7ED;color:#C2410C;">est.</span> editable assumption — not booked dollars
        </div>
      </div>
    </div>

    <!-- Data notes -->
    <div v-if="actuals && actuals.notes" class="px-4 pb-4">
      <details class="text-xs" style="color:#626264;">
        <summary class="cursor-pointer">Data notes &amp; caveats</summary>
        <ul class="list-disc ml-5 mt-1">
          <li v-for="(n, i) in actuals.notes" :key="i">{{ n }}</li>
        </ul>
      </details>
    </div>
  </div>
</template>

<script>
import { formatProgram } from '@/components/Coding/Coding_Operations_V2_Views.js';

// Program Economics tab — two grain-clean funnels (opportunity/effort + code/
// value) bridged by codes-per-opp, plus an editable scenario simulator.
// Fetches actuals from POST /api/coding/economics/, seeds levers from the
// measured rates, and computes the projection client-side. North-star =
// realized $; coder cost is an input lever. Financials use flagged ASSUMPTION
// levers (recode %, $/code) pending the claims recode rate and ACO waterfall.
const TEAL = '#0E8C9E';

const DEFAULT_REBILL_RATE = 0.4;       // % of accepted suspects actually re-billed/captured (ACO follow-through ~40%; measurable from claims)
const DEFAULT_DOLLAR_PER_RAF = 10400;  // $ per 1.0 RAF / member-yr (2025 MA national base ≈ $10,402)
const DEFAULT_ACO_REALIZATION = 0.10;  // ACO net realization after ±3%/+3% caps, intensity, shared-savings (assumption)
const DEFAULT_HOURLY_COST = 15;        // offshore coder $/hr (1LR & 2LR offshore; US QA tiers are <5% of coder-days)
const DEFAULT_OVERHEAD = 0.25;         // program overhead on labor — mgmt, QA oversight, tooling, recruiting (assumption; ~15–40%)
const HOURS_PER_DAY = 8;             // charts/coder-day -> charts/hour
const REVIEWS_PER_DAY_FALLBACK = 8;    // when no human-attributed throughput exists (e.g. PY2025)

// Projected-outcome band: ACO realization is the dominant, least-grounded input, so the
// bottom line is shown across a low/base/high realization range instead of one false point.
// Base = the editable ACO-realization lever; low/high scale it (×0.5 / ×1.5 → 5/10/15% at the 10% default).
const REALIZATION_LOW_FACTOR = 0.5;
const REALIZATION_HIGH_FACTOR = 1.5;

// One color language shared by all three funnels. Each hue carries a single
// fixed meaning, so the same status reads identically across the opportunity,
// task and code views:
//   volume   structural totals — funnel inputs / denominators
//   active   open, in-progress work
//   hold     paused
//   closed   closed without value (resolved administratively)
//   neutral  pending / not yet decided
//   good     success & captured value (brand teal)
//   goodQA   the QA-concurred subset of "good" (lighter teal so the subset reads)
//   lost     lost / removed (muted clay — deliberately not an alarm red)
const PALETTE = {
  volume: '#5E7E97',
  active: '#93B2CB',
  hold: '#A9BAC9',
  closed: '#8B97A4',
  neutral: '#CFD6DC',
  good: '#0E8C9E',
  goodQA: '#6FC1CC',
  lost: '#CD9488',
};

export default {
  name: 'CodingProgramEconomics',
  data() {
    return {
      program: 'Suspect',
      programValues: ['Suspect', 'Resub', 'Early Start'],
      year: 2026,
      yearOptions: [
        { label: 'All', value: null },
        { label: '2025', value: 2025 },
        { label: '2026', value: 2026 },
      ],
      scope: 'activity',
      scopeOptions: [
        { label: 'Performance year', value: 'performance' },
        { label: 'Activity year', value: 'activity' },
      ],
      includeExpired: false,
      infoHover: false,
      infoPinned: false,
      hoveredLeverKey: null,
      hoveredOutputKey: null,
      focusedLeverKey: null,
      taskLegend: [
        { key: 'active', label: 'active (open)', color: PALETTE.active },
        { key: 'on_hold', label: 'on hold', color: PALETTE.hold },
        { key: 'succeeded', label: 'succeeded', color: PALETTE.good },
        { key: 'closed_out', label: 'closed-out', color: PALETTE.closed },
        { key: 'skipped', label: 'skipped', color: PALETTE.neutral },
        { key: 'expired', label: 'expired', color: PALETTE.lost },
      ],
      hiddenStatuses: [],
      loading: false,
      error: null,
      actuals: null,
      cohort: null,
      scenario: {},
      // Scenario basis: 'projection' = apply observed rates to the FULL cohort (end-of-year,
      // everything worked); 'current' = just the opportunities reviewed so far (reproduces actuals).
      mode: 'projection',
      modeOptions: [
        { label: 'Projected (year-end)', value: 'projection' },
        { label: 'Actuals (to date)', value: 'current' },
      ],
      reviewedOpps: 0,
    };
  },
  computed: {
    palette() {
      return PALETTE;
    },
    producedSplit() {
      const row = ((this.actuals && this.actuals.opp3) || []).find(r => r.key === 'produced');
      if (!row || !row.bands) return null;
      const band = k => (row.bands.find(b => b.key === k) || {}).count || 0;
      return { completed: band('produced_completed'), open: band('produced_open') };
    },
    programOptions() {
      // Reuse Operations V2's shared label map so both tabs display "PVP" while
      // the backend keeps filtering the real 'Suspect' PROGRAM value.
      return this.programValues.map(v => ({ value: v, label: formatProgram(v) }));
    },
    maxTierOpened() {
      const t = this.actuals && this.actuals.tasks3;
      return t && t.length ? Math.max(1, ...t.map(x => this.visibleOpened(x))) : 1;
    },
    baseTierLabel() {
      const t = (this.actuals && this.actuals.tasks3) || [];
      return t.length ? t[0].tier : '1LR';
    },
    taskAggregate() {
      const agg = {};
      this.taskLegend.forEach(s => { agg[s.key] = 0; });
      ((this.actuals && this.actuals.tasks3) || []).forEach(t => {
        this.taskLegend.forEach(s => { agg[s.key] += (t[s.key] || 0); });
      });
      agg.opened = this.visibleOpened(agg);
      return agg;
    },
    leverDefs() {
      const avgRaf = (this.actuals && this.actuals.financial && this.actuals.financial.avg_raf_per_code) || 0;
      return [
        {
          key: 'opportunities', label: 'Opportunities worked', step: 1000, thousands: true, divider: 'Volume & yield',
          tip: 'Opportunities worked. Set by the basis toggle: Projected = full cohort, Actuals = reviewed so far.',
        },
        {
          key: 'codesPerOpp', label: 'Codes per reviewed opp', step: 0.01,
          tip: 'Codes found ÷ reviewed opportunities.',
        },
        {
          key: 'acceptRate', label: 'Accept rate', step: 1, suffix: '%', percent: true,
          tip: 'Accepted ÷ resolved codes. Accepted = QA-passed + auto-accepted; resolved = accepted + rejected.',
        },
        {
          key: 'rebillRate', label: 'Re-bill / capture rate', step: 1, suffix: '%', percent: true, assumption: true,
          tip: 'Share of accepted codes the provider re-bills on a performance-year claim. Not yet measured (pending claims join).',
        },
        {
          key: 'avgRafPerCode', label: 'Avg RAF / code', step: 0.001, divider: '$ per captured code',
          tip: `Average V28 (CNA) RAF coefficient of captured codes, measured from the data (~${avgRaf} here).`,
        },
        {
          key: 'dollarPerRaf', label: '$ per RAF', step: 100, prefix: '$', thousands: true, assumption: true,
          tip: 'Dollar value of 1.0 RAF per member-year. Default = 2025 MA national base.',
        },
        {
          key: 'acoRealization', label: 'ACO realization', step: 1, suffix: '%', percent: true, assumption: true,
          tip: 'Share of the gross RAF dollar an ACO nets, after coding-intensity caps (MSSP +3% / REACH ±3%) and shared-savings flow-through.',
        },
        {
          key: 'dollarPerCode', label: '$ / captured code (net)', derived: true, assumption: true,
          tip: 'Net $ per re-billed code = avg RAF × $/RAF × ACO realization. Single-year.',
        },
        {
          key: 'reviewsPerOpp', label: 'Reviews per opportunity', step: 0.05, divider: 'Effort & cost',
          tip: 'Completed chart reviews (all tiers) ÷ reviewed opportunities.',
        },
        {
          key: 'reviewsPerDay', label: 'Reviews / coder-day', step: 0.5,
          tip: 'Charts reviewed per coder-day, blended across tiers.',
        },
        {
          key: 'hourlyCost', label: 'Coder rate', step: 5, prefix: '$', suffix: '/hr',
          tip: 'Fully-loaded offshore coder cost per hour.',
        },
        {
          key: 'overheadRate', label: 'Overhead', step: 5, suffix: '%', percent: true, assumption: true,
          tip: 'Program overhead on reviewer labor (management, QA oversight, tooling, recruiting).',
        },
      ];
    },
    dollarPerCode() {
      // Derived net $/captured-code = avg RAF × $/RAF × ACO realization (the three editable inputs).
      const s = this.scenario;
      return (s.avgRafPerCode || 0) * (s.dollarPerRaf || 0) * (s.acoRealization || 0);
    },
    dollarPerCodeTip() {
      const s = this.scenario;
      return `Avg RAF (${Number(s.avgRafPerCode || 0).toFixed(3)}) × $${Number(s.dollarPerRaf || 0).toLocaleString()}/RAF `
        + `× ACO realization (${this.pct(s.acoRealization)}) = ${this.money(this.dollarPerCode)}`;
    },
    scenarioResults() {
      const s = this.scenario;
      const found = (s.opportunities || 0) * (s.codesPerOpp || 0);
      const accepted = found * (s.acceptRate || 0);
      const recoded = accepted * (s.rebillRate || 0);
      const realized = recoded * this.dollarPerCode;
      const cost = this.operatingCost;
      return {
        found, accepted, recoded, realized, cost,
        net: realized - cost,
        roi: cost ? realized / cost : 0,
      };
    },
    operatingCost() {
      // Effort-driven labor cost, starting from opportunities (not codes — you
      // review the chart whether or not it yields a code): opportunities ×
      // reviews-per-opp = chart reviews; ÷ reviews-per-day = coder-days;
      // × $/hr × 8h = direct labor; × (1 + overhead) for program indirect.
      const s = this.scenario;
      const totalReviews = (s.opportunities || 0) * (s.reviewsPerOpp || 0);
      const perDay = s.reviewsPerDay || 0;
      if (perDay <= 0) return 0;
      const coderDays = totalReviews / perDay;
      const labor = coderDays * (s.hourlyCost || 0) * HOURS_PER_DAY;
      return labor * (1 + (s.overheadRate || 0));
    },
    // Realization-invariant outputs — the funnel chain plus operating cost. The
    // realization-sensitive bottom line (value, net, ROI) is shown as a band; see
    // scenarioBand, so the dominant assumption reads as a range, not a false point.
    outputRows() {
      const r = this.scenarioResults;
      const s = this.scenario;
      const n = (v, d = 0) => Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: d });
      const totalReviews = (s.opportunities || 0) * (s.reviewsPerOpp || 0);
      const coderDays = (s.reviewsPerDay || 0) > 0 ? totalReviews / s.reviewsPerDay : 0;
      return [
        {
          label: 'Codes found', value: n(r.found),
          tip: `Opportunities (${n(s.opportunities)}) × Codes/opp (${n(s.codesPerOpp, 3)}) = ${n(r.found)}`,
        },
        {
          label: 'Codes accepted', value: n(r.accepted),
          tip: `Codes found (${n(r.found)}) × Accept rate (${this.pct(s.acceptRate)}) = ${n(r.accepted)}`,
        },
        {
          label: 'Recoded (re-billed)', value: n(r.recoded), estimate: true,
          tip: `Codes accepted (${n(r.accepted)}) × Re-bill rate (${this.pct(s.rebillRate)}) = ${n(r.recoded)}`,
        },
        {
          label: 'Operating cost', value: this.moneyShort(r.cost), estimate: true,
          tip: `Opportunities (${n(s.opportunities)}) × Reviews/opp (${n(s.reviewsPerOpp, 2)}) ÷ Reviews/day (${n(s.reviewsPerDay, 1)}) `
            + `× $${n(s.hourlyCost)}/hr × ${HOURS_PER_DAY}h × (1 + overhead ${this.pct(s.overheadRate)}) = ${this.money(r.cost)}  (≈ ${n(coderDays)} coder-days, ${n(totalReviews)} reviews)`,
        },
      ];
    },
    // Bottom line across a low / base / high ACO-realization band (the dominant
    // input). recoded and operating cost don't move with realization, so only
    // $/captured code, realized value, net and ROI differ across the columns.
    scenarioBand() {
      const r = this.scenarioResults;
      const s = this.scenario;
      const perRaf = (s.avgRafPerCode || 0) * (s.dollarPerRaf || 0);
      const baseReal = s.acoRealization || 0;
      return [
        { key: 'low', label: 'Conservative', realization: baseReal * REALIZATION_LOW_FACTOR },
        { key: 'base', label: 'Base', realization: baseReal },
        { key: 'high', label: 'Optimistic', realization: baseReal * REALIZATION_HIGH_FACTOR },
      ].map(c => {
        const dollarPerCode = perRaf * c.realization;
        const realized = r.recoded * dollarPerCode;
        const net = realized - r.cost;
        return { ...c, dollarPerCode, realized, net, roi: r.cost ? realized / r.cost : 0 };
      });
    },
    bandMetrics() {
      return [
        { key: 'dollarPerCode', label: '$ / captured code', fmt: 'money', tip: 'avg RAF × $/RAF × ACO realization (this column)' },
        { key: 'realized', label: 'Realized value', fmt: 'moneyShort', tone: 'teal', tip: 'Recoded codes × $/captured code' },
        { key: 'net', label: 'Net value', fmt: 'moneyShort', tone: 'net', tip: 'Realized value − operating cost' },
        { key: 'roi', label: 'Value-to-cost', fmt: 'roi', tone: 'net', tip: 'Realized value ÷ operating cost (gross). 1.5× ≈ $1.50 of value recaptured per $1 spent — a +45% net return (see Net value), not a profit multiple.' },
      ];
    },
    // Base-case column of the realization band — feeds the headline net value / value-to-cost.
    baseBand() {
      return this.scenarioBand.find(c => c.key === 'base')
        || { dollarPerCode: 0, realized: 0, net: 0, roi: 0 };
    },
  },
  mounted() {
    this.fetchActuals();
  },
  methods: {
    async fetchActuals() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await this.$axios.post('/api/coding/economics/', {
          program: this.program,
          year: this.year,
          scope: this.scope,
          includeExpired: this.includeExpired,
        });
        this.actuals = data;
        this.cohort = data.cohort;
        this.seedScenario();
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not load program economics.';
        this.actuals = null;
      } finally {
        this.loading = false;
      }
    },
    seedScenario() {
      const r = this.actuals.rates;
      const avgRaf = (this.actuals.financial && this.actuals.financial.avg_raf_per_code) || 0;
      // Projection from WORK DONE SO FAR — everything scales on opportunities REVIEWED
      // (1LR completed), using COMPLETED tasks (succeeded + closed_out), so cost and value
      // share one basis. (Costing the unreviewed backlog against codes-so-far is the bug
      // that made ROI look negative.) Crank "opportunities worked" up to project the rest.
      const reviewedRow = (this.actuals.opp3 || []).find(x => x.key === 'reviewed');
      const reviewed = (reviewedRow && reviewedRow.count) || 0;
      const rateByTier = {};
      (this.actuals.throughput || []).forEach(row => { rateByTier[row.tier] = row.tasks_per_coder_day || 0; });
      let reviewsDone = 0;
      let coderDays = 0;
      (this.actuals.tasks3 || []).forEach(t => {
        const completed = (t.succeeded || 0) + (t.closed_out || 0);
        reviewsDone += completed;
        const rate = rateByTier[t.tier] || 0;
        if (rate > 0) coderDays += completed / rate;
      });
      const reviewsPerOpp = reviewed > 0 ? Math.round((reviewsDone / reviewed) * 100) / 100 : 0;
      const reviewsPerDay = coderDays > 0
        ? Math.round((reviewsDone / coderDays) * 10) / 10
        : REVIEWS_PER_DAY_FALLBACK;
      this.reviewedOpps = reviewed;
      // Same observed rates in both modes — only the opportunity count differs:
      // projection = full cohort (everything worked), current = reviewed so far.
      const opportunities = this.mode === 'projection'
        ? (this.cohort.n_opportunities || reviewed)
        : reviewed;
      this.scenario = {
        opportunities,
        // Round seeds to the precision the tooltips display, so shown operands == values used.
        codesPerOpp: Math.round((r.codes_per_opportunity || 0) * 1000) / 1000,
        acceptRate: Math.round((r.accept_rate || 0) * 1000) / 1000,
        rebillRate: r.recode_rate != null ? r.recode_rate : DEFAULT_REBILL_RATE,
        // $/captured-code decomposed into three editable inputs (avg RAF × $/RAF × ACO realization).
        avgRafPerCode: Math.round(avgRaf * 1000) / 1000,
        dollarPerRaf: DEFAULT_DOLLAR_PER_RAF,
        acoRealization: DEFAULT_ACO_REALIZATION,
        reviewsPerOpp,
        reviewsPerDay,
        hourlyCost: DEFAULT_HOURLY_COST,
        overheadRate: DEFAULT_OVERHEAD,
      };
    },
    resetScenario() {
      if (this.actuals) this.seedScenario();
    },
    setMode(v) {
      // Toggle basis without re-fetching: only the opportunity count changes between
      // projection (full cohort) and current (reviewed so far); edited rates are kept.
      this.mode = v;
      if (this.scenario && this.cohort) {
        this.scenario.opportunities = v === 'projection'
          ? (this.cohort.n_opportunities || this.reviewedOpps)
          : this.reviewedOpps;
      }
    },
    setYear(v) {
      this.year = v;
      this.fetchActuals();
    },
    setScope(v) {
      this.scope = v;
      this.fetchActuals();
    },
    funnelRows(arr) {
      const rows = arr || [];
      const top = rows.length ? rows[0].count : 0;
      return rows.map((s, i) => {
        const banded = Array.isArray(s.bands) && s.bands.length > 0;
        return {
          ...s,
          isTop: i === 0,
          pct: top ? Math.max(3, Math.round((s.count / top) * 100)) : 3,
          // share = % of the funnel's first (largest) row — the single metric
          // shown across all three funnels. Step-to-step conversion wouldn't
          // apply to the code funnel (its rows are parallel outcomes of
          // "found"), so we standardise on share-of-top everywhere. The first
          // row is the 100% base and is left blank. A BANDED row (e.g.
          // "Produced ≥1 code", which spans completed AND open opps) renders
          // its split as bar segments.
          share: top ? Math.round((s.count / top) * 100) : null,
          bands: banded
            ? s.bands.map(b => ({
              ...b,
              color: this.bandColor(b.key),
              pctOfRow: s.count ? (b.count / s.count) * 100 : 0,
            }))
            : null,
        };
      });
    },
    bandColor(key) {
      return { produced_completed: PALETTE.good, produced_open: PALETTE.active }[key] || PALETTE.neutral;
    },
    // Opportunity-funnel % tooltip — share of all opportunities (the cohort top).
    convTitle(s) {
      if (s.isTop) return 'Top of the funnel — 100%';
      if (s.bands) return `${s.share}% of all opportunities (spans completed + open opps)`;
      return `${s.share}% of all opportunities`;
    },
    tierSharePct(t) {
      // Task tiers as a share of the base tier (1LR) — the QA escalation rate.
      const base = this.visibleOpened(((this.actuals && this.actuals.tasks3) || [])[0] || {});
      return base ? Math.round((this.visibleOpened(t) / base) * 100) : 0;
    },
    visibleOpened(t) {
      return this.taskLegend.reduce(
        (sum, s) => (this.hiddenStatuses.includes(s.key) ? sum : sum + (t[s.key] || 0)), 0);
    },
    toggleStatus(key) {
      const i = this.hiddenStatuses.indexOf(key);
      if (i === -1) this.hiddenStatuses.push(key);
      else this.hiddenStatuses.splice(i, 1);
    },
    taskSegments(t) {
      const visible = this.visibleOpened(t) || 1;
      return this.taskLegend
        .filter(seg => !this.hiddenStatuses.includes(seg.key))
        .map(seg => ({ ...seg, count: t[seg.key] || 0, pct: ((t[seg.key] || 0) / visible) * 100 }))
        .filter(seg => seg.count > 0);
    },
    codeColor(key) {
      return {
        found: PALETTE.volume, accepted: PALETTE.good, accepted_qa: PALETTE.goodQA,
        rejected: PALETTE.lost, still_open: PALETTE.neutral, lost: PALETTE.closed,
      }[key] || PALETTE.active;
    },
    codeTip(key) {
      return {
        found: 'Codes a 1LR coder suggested from the chart',
        accepted: 'Codes sent to the practice — QA-passed plus auto-accepted (opp resolved without explicit QA)',
        accepted_qa: 'The subset of accepted codes a QA reviewer explicitly concurred with',
        rejected: 'A QA reviewer disagreed — code not supported, removed',
        still_open: 'Opportunity still active; code not yet finalized',
        lost: 'Opportunity expired before the code was finalized — never sent',
      }[key] || '';
    },
    money(v) {
      return (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    },
    // Compact $ for the at-a-glance outputs ($5.80M); the row tooltip carries
    // the full-precision figure via money().
    moneyShort(v) {
      const a = Math.abs(v || 0);
      const sign = (v || 0) < 0 ? '-' : '';
      if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
      if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}k`;
      return this.money(v);
    },
    // Rates read as percentages in the UI while staying decimals in the model.
    pct(v) {
      return `${(Number(v || 0) * 100).toFixed(1)}%`;
    },
    // Render one cell of the realization band (scenarioBand × bandMetrics).
    bandCellValue(m, c) {
      const v = c[m.key];
      if (m.fmt === 'moneyShort') return this.moneyShort(v);
      if (m.fmt === 'roi') return v ? `${v.toFixed(1)}×` : '—';
      return this.money(v);
    },
    bandCellStyle(m, c) {
      const weight = c.key === 'base' ? 'font-weight:600;' : '';
      let color = '#353537';
      if (m.tone === 'teal') color = TEAL;
      else if (m.tone === 'net') color = c.net >= 0 ? '#15803D' : '#DC2626';
      return `color:${color};${weight}`;
    },
    // For percent levers the input shows whole-ish percent (76.1) while the
    // scenario keeps the decimal (0.761); non-percent levers pass through.
    leverDisplay(lever) {
      const v = this.scenario[lever.key];
      if (v == null || v === '') return '';
      if (lever.percent) return Math.round(v * 1000) / 10;
      // Thousands-formatted levers show commas at rest, but raw digits while being
      // edited so the cursor doesn't jump as the value reformats mid-keystroke.
      if (lever.thousands) return this.focusedLeverKey === lever.key ? v : Number(v).toLocaleString('en-US');
      return v;
    },
    onLeverInput(lever, raw) {
      const cleaned = lever.thousands ? String(raw).replace(/,/g, '') : raw;
      const num = cleaned === '' ? null : Number(cleaned);
      if (num == null || Number.isNaN(num)) { this.scenario[lever.key] = null; return; }
      this.scenario[lever.key] = lever.percent ? num / 100 : num;
    },
  },
};
</script>

<style scoped>
/* Hide native number-input spinners so number-type and text-type ("thousands")
   lever inputs share the same right edge — the spinner otherwise reserves space
   on the right and pulls number-type values left of the text-type ones. Arrow
   keys still step the value. */
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
